import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Map, List } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from '../../../actions/index'
import { createTitleGroupId } from '../../../services/content/index'
import { TYPE_CONTENT_COURSE } from '../../../services/content-type/index'
import { getAuthIsLoggedIn } from '../../../services/auth/index'
import { getPrimary as getPrimaryLanguage } from '../../../services/languages/index'
import {
  COURSE_MODULE_MARK_COMPLETE,
  USER_STATE_CREATE_OR_UPDATE,
} from '../../../services/detail/query'
import {
  USER_STATE_PROGRESS_ITEM_TYPE_VIDEO,
  USER_STATE_PROGRESS_ITEM_TYPE_ASSET,
  COURSE_STATE_COMPLETE,
  COURSE_STATE_IN_PROGRESS,
} from '../../../services/detail/index'
import Button, { TYPES } from '../../Button/index'
import CheckIcon from '../../Icon/icons/Check/index'
import ReplayIcon from '../../Icon/icons/Replay/index'
import ChevronUpIcon from '../../Icon/icons/ChevronUp/index'
import ChevronDownIcon from '../../Icon/icons/ChevronDown/index'
import {
  checkIfCourseWithoutProgress,
  getCourseModuleItemsData,
  getCourseModuleTitle,
} from '../utils'
import DetailPageV3CourseModuleDescription from './DetailPageV3CourseModuleDescription'
import DetailPageV3CourseModuleContent from './DetailPageV3CourseModuleContent'

/**
 * @param {Object} props - The props
 * @param {Map} props.auth - The auth data
 * @param {Map} props.moduleData - The course module data
 * @param {number} props.moduleNumber - The module number
 * @param {number} props.courseContentId - The course content ID
 * @param {function} props.onCourseStateChange - The function to handle course state change
 * @returns {JSX.Element} DetailPageV3CourseModule
 */

const DetailPageV3CourseModule = (props) => {
  const {
    auth,
    moduleData = Map(),
    staticText,
    moduleNumber,
    courseContentId,
    onCourseStateChange,
    userLanguage,
  } = props

  const jwt = auth.get('jwt')
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const moduleId = moduleData.get('id')
  const moduleState = moduleData.getIn(['courseModuleProgress', 'state'])
  const completed = moduleState === COURSE_STATE_COMPLETE
  const isPublished = moduleData.get('publishedAtDate') && new Date(moduleData.get('publishedAtDate')) <= new Date()
  const moduleItemsData = getCourseModuleItemsData(moduleData)
  const displayTitle = getCourseModuleTitle(moduleData, userLanguage, staticText)
  const moduleAssets = moduleData.get('contentAssetReferences', List()).map((ref) => ref.get('asset', Map()))
  const moduleTitleGroupId = moduleData.get('titleGroupId')
  const hasAllItems = moduleItemsData.size > 0
    && moduleItemsData.every((itemData) => itemData.getIn(['item', 'contentId']))

  const [isExpanded, setIsExpanded] = useState(false)

  const showProgressButton = !!moduleId
    && isLoggedIn
    && hasAllItems
    && !checkIfCourseWithoutProgress(courseContentId)

  const [markComplete, {
    loading: loadingMarkComplete,
  }] = useMutation(COURSE_MODULE_MARK_COMPLETE, {
    variables: {
      titleGroupId: createTitleGroupId(courseContentId, TYPE_CONTENT_COURSE),
      moduleTitleGroupId,
    },
    context: {
      jwt,
    },
  })

  const [userStateCreateOrUpdate, {
    loading: loadingUserStateCreateOrUpdate,
  }] = useMutation(USER_STATE_CREATE_OR_UPDATE, {
    variables: {
      parentContentTitleGroupId: createTitleGroupId(courseContentId, TYPE_CONTENT_COURSE),
    },
    context: {
      jwt,
    },
  })

  const markReset = async () => {
    const progressItemIds = moduleItemsData.map((itemData) => itemData.getIn(['item', 'titleGroupId']))
    await Promise.all(
      [
        ...progressItemIds.map((progressItemId) => userStateCreateOrUpdate({
          variables: {
            progressItemId,
            state: COURSE_STATE_IN_PROGRESS,
            progressItemType: USER_STATE_PROGRESS_ITEM_TYPE_VIDEO,
          },
        })),
        ...moduleAssets.map((asset) => userStateCreateOrUpdate({
          variables: {
            progressItemId: asset.get('uuid'),
            state: COURSE_STATE_IN_PROGRESS,
            progressItemType: USER_STATE_PROGRESS_ITEM_TYPE_ASSET,
          },
        })),
      ],
    )
  }

  const handleStateChange = async () => {
    if (completed) {
      await markReset()
    } else {
      await markComplete()
    }
    onCourseStateChange()
  }

  const moduleClassName = [
    'detail-page-v3__course-module',
    completed ? 'detail-page-v3__course-module--completed' : null,
    isExpanded ? 'detail-page-v3__course-module--expanded' : null,
  ].filter((cls) => !!cls).join(' ')

  const moduleContentClassName = [
    'detail-page-v3__course-module-content',
    isExpanded ? 'detail-page-v3__course-module-content--expanded' : null,
  ].filter((cls) => !!cls).join(' ')

  return (
    <div className={moduleClassName} key={moduleId}>
      <div className="detail-page-v3__course-module-header detail-page-v3__course-module-header--desktop">
        {completed
          ? <CheckIcon size={44} color="#AFBDC6"/>
          : <div className={`detail-page-v3__course-module-number detail-page-v3__course-module-number--${moduleNumber}`}>
            {moduleNumber}
          </div>
        }
        <div className="detail-page-v3__course-module-title-wrapper">
          <h3 className="detail-page-v3__course-module-title">
            {displayTitle}
          </h3>
          {showProgressButton && (
            <Button
              className="detail-page-v3__course-module-header-button"
              type={TYPES.GHOST}
              onClick={handleStateChange}
              disabled={loadingMarkComplete || loadingUserStateCreateOrUpdate}
            >
              {completed ? <ReplayIcon size={20} /> : <CheckIcon size={20} />}
              {' '}
              <span>
                {completed ? staticText.get('startFromThisStepAgain') : staticText.get('markStepAsComplete')}
              </span>
            </Button>
          )}
        </div>
        <Button
          className="detail-page-v3__course-module-toggle-button"
          type={TYPES.GHOST}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? <ChevronUpIcon size={32} color="#7C8C9D"/> : <ChevronDownIcon size={32} color="#7C8C9D"/>}
        </Button>
      </div>
      <Button
        className="detail-page-v3__course-module-header detail-page-v3__course-module-header--mobile"
        type={TYPES.GHOST}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {completed
          ? <CheckIcon size={44} color="#AFBDC6"/>
          : <div className={`detail-page-v3__course-module-number detail-page-v3__course-module-number--${moduleNumber}`}>
            {moduleNumber}
          </div>
        }
        <div className="detail-page-v3__course-module-title-wrapper">
          <h3 className="detail-page-v3__course-module-title">
            {displayTitle}
          </h3>
        </div>
        {isExpanded ? <ChevronUpIcon size={24} color="#7C8C9D"/> : <ChevronDownIcon size={24} color="#7C8C9D"/>}
      </Button>
      <div className={moduleContentClassName}>
        <DetailPageV3CourseModuleDescription moduleData={moduleData} staticText={staticText} />
        <DetailPageV3CourseModuleContent
          moduleState={moduleState}
          moduleItemsData={moduleItemsData}
          isPublished={isPublished}
          moduleAssets={moduleAssets}
          staticText={staticText}
          courseContentId={courseContentId}
          onCourseStateChange={onCourseStateChange}
          handleStateChange={handleStateChange}
          showProgressButton={showProgressButton}
          isButtonDisabled={loadingMarkComplete || loadingUserStateCreateOrUpdate}
        />
      </div>
    </div>
  )
}

export default connectRedux((state) => ({
  auth: state.auth,
  userLanguage: getPrimaryLanguage(state.user.getIn(['data', 'language'])),
  upstreamContext: state.upstreamContext,
  staticText: state.staticText.getIn(['data', 'detailPageV3', 'data'], Map()),
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    setEventDefault: actions.eventTracking.setEventDefault,
  }
})(DetailPageV3CourseModule)
